import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Row, Col, Modal, ModalHeader, ModalBody } from 'reactstrap';
import axios from 'axios';
import { api } from '../../../../../../../config';
import { getAuthToken } from '../../../../../../../auth/tokenvalidation';
import notify from '../../../../../../../utils/notify';
import ActionButton from '../../../../../../../Components/Common/ActionButton';
import {
  FormActions,
  FormGrid,
  SelectField,
  TextField,
} from '../../../../../../../Components/Form';
import {
  DetailItem,
  SectionCard,
  RespondNotesEditor,
} from '../../../../../../../shared/offer-domain/ui';
import { COUNTER_ACCENT } from '../../../../../../../shared/offer-domain/constants';
import {
  pickLatestCounter,
  isSameId,
  isSameHtml,
  CounterDetailItem,
  CounterValueBlock,
  resolvePaymentSelectLabel,
  resolvePaymentCurrencyLabel,
  PAYMENT_TERM_FIELDS,
  COMPLIANCE_FIELDS,
  DELAY_PENALTY_TERM_FIELDS,
  isFreeTextField,
  isNumberField,
  isSameNumber,
  formatCounterFieldValue,
  getCounterFieldValue,
  buildFieldCounterRow,
  isGroupAgreeField,
  isFieldAgreeable,
  getAgreedFinal,
  getAgreedFinalHtml,
  isPositiveAmount,
  resolveDelayPenaltyAmountText,
} from '../../../../../../../shared/offer-domain/payment';
import AgreeLockButton from '../../../../../../../shared/offer-domain/clause/AgreeLockButton';

export { pickLatestCounter };

const DEFAULT_AGREE_NOTICE =
  'If you do not respond, the status is considered Agree. You can still respond later if you disagree.';

const COUNTER_BADGE_STYLE = {
  backgroundColor: COUNTER_ACCENT,
  color: '#fff',
};

const DefaultAgreeNoticeAlert = () => (
  <Alert
    color='info'
    className='d-flex align-items-start gap-2 border-0 bg-light text-primary mb-3'
  >
    <i className='ri-information-line flex-shrink-0 mt-1'></i>
    <span className='small'>{DEFAULT_AGREE_NOTICE}</span>
  </Alert>
);

const SectionTitle = ({ title, subtitle, action }) => (
  <div className='d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3'>
    <div>
      <h5 className='fw-bold text-dark mb-1'>{title}</h5>
      {subtitle && <p className='text-muted small mb-0'>{subtitle}</p>}
    </div>
    {action}
  </div>
);

const unwrapItems = result => {
  const data = result?.data ?? result;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.Items)) return data.Items;
  if (Array.isArray(data)) return data;
  return [];
};

const itemName = item => item?.name ?? item?.Name ?? '';

const mapSelectItems = (list, getLabel) =>
  (Array.isArray(list) ? list : [])
    .map(item => ({
      value: item?.id ?? item?.Id ?? '',
      label: getLabel(item),
    }))
    .filter(item => item.value && item.label);

const fetchJson = async (url, token) => {
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) throw new Error(response.statusText);
  return response.json();
};

const PaymentComplianceView = ({
  procurement,
  procurementMitraId = null,
  // Mitra confirm tarif invoice standar (type 22 isConfirmed).
  isDelayPenaltyConfirmed = false,
  delayPenaltyRespond = null,
  paymentRespond = null,
  complianceRespond = null,
  onRefreshProcurement = null,
  // Clarification: field pilihan = 1 thread kelompok (paymentRespond /
  // complianceRespond, type 5/6) + Agree & Lock per field (type 8-13) +
  // 1 tombol Counter kelompok; field free-text = thread sendiri (11/14/15)
  // + Agree & Lock per field dengan Final (type 19-21).
  renderFieldRespond = null,
  canCounterField = null,
  canCounterGroup = null,
  fieldAgreements = {},
  onAgreeField = null,
  isAgreeSaving = false,
}) => {
  const token = getAuthToken();
  const isPerField = typeof renderFieldRespond === 'function';
  const [invoiceQuantityOptions, setInvoiceQuantityOptions] = useState([]);
  const [modeOptions, setModeOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [lawOptions, setLawOptions] = useState([]);
  const [arbitrationOptions, setArbitrationOptions] = useState([]);
  const [isSavingCounter, setIsSavingCounter] = useState(false);
  // { title, groupDefs, defs, draft } — counter yang sedang diedit Mitra
  // (satu field free-text, atau semua field pilihan satu kelompok).
  const [counterEdit, setCounterEdit] = useState(null);

  const latestDelayPenaltyCounter = useMemo(
    () =>
      pickLatestCounter(
        procurement?.counterDelayPenaltyTerm,
        procurementMitraId
      ),
    [procurement?.counterDelayPenaltyTerm, procurementMitraId]
  );
  const latestPaymentCounter = useMemo(
    () =>
      pickLatestCounter(procurement?.counterPaymentTerm, procurementMitraId),
    [procurement?.counterPaymentTerm, procurementMitraId]
  );
  const latestComplianceCounter = useMemo(
    () => pickLatestCounter(procurement?.counterCompliance, procurementMitraId),
    [procurement?.counterCompliance, procurementMitraId]
  );

  const saveCounter = async (endpoint, payload, existingId) => {
    const procurementFK = procurement?.id;
    if (!procurementFK) {
      notify.error(
        'The procurement could not be identified. Return to the list and open it again.'
      );
      return false;
    }
    if (isSavingCounter) return false;

    setIsSavingCounter(true);
    try {
      const url = existingId
        ? `${api.API_URL}/${endpoint}/${existingId}`
        : `${api.API_URL}/${endpoint}`;
      const request = existingId ? axios.put : axios.post;
      await request(
        url,
        { procurementFK, ...payload },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (typeof onRefreshProcurement === 'function') {
        await onRefreshProcurement();
      }
      notify.success(
        existingId
          ? 'Counter offer updated successfully.'
          : 'Counter offer saved successfully.'
      );
      return true;
    } catch (error) {
      notify.apiError(
        error,
        existingId ? 'update the counter offer' : 'save the counter offer'
      );
      return false;
    } finally {
      setIsSavingCounter(false);
    }
  };

  useEffect(() => {
    if (!token) return;

    const loadInvoiceQuantity = async () => {
      try {
        const result = await fetchJson(
          `${api.API_URL}/invoicing-quantity`,
          token
        );
        setInvoiceQuantityOptions(
          mapSelectItems(unwrapItems(result), itemName)
        );
      } catch {
        setInvoiceQuantityOptions([]);
      }
    };

    const loadCurrency = async () => {
      try {
        const result = await fetchJson(`${api.API_URL}/paymentcurrency`, token);
        setCurrencyOptions(
          mapSelectItems(unwrapItems(result), item =>
            item?.currencyName || item?.CurrencyName
              ? `${item.currencyName ?? item.CurrencyName} (${item.currencyCode ?? item.CurrencyCode})`
              : itemName(item)
          )
        );
      } catch {
        setCurrencyOptions([]);
      }
    };

    const loadLaw = async () => {
      try {
        const result = await fetchJson(`${api.API_URL}/law`, token);
        setLawOptions(mapSelectItems(unwrapItems(result), itemName));
      } catch {
        setLawOptions([]);
      }
    };

    const loadArbitration = async () => {
      try {
        const result = await fetchJson(
          `${api.API_URL}/law/getAll-arbitration`,
          token
        );
        setArbitrationOptions(mapSelectItems(unwrapItems(result), itemName));
      } catch {
        setArbitrationOptions([]);
      }
    };

    loadInvoiceQuantity();
    loadCurrency();
    loadLaw();
    loadArbitration();
  }, [token]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    const loadModes = async () => {
      try {
        const result = await fetchJson(
          `${api.API_URL}/invoicing-quantity/getAll-ModeInvoicingQuantity`,
          token
        );
        if (cancelled) return;
        setModeOptions(mapSelectItems(unwrapItems(result), itemName));
      } catch {
        if (!cancelled) setModeOptions([]);
      }
    };

    loadModes();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const originalCurrencyLabel = resolvePaymentCurrencyLabel(
    procurement?.paymentCurrency,
    procurement?.paymentCurrencyId,
    currencyOptions
  );
  const counterCurrencyLabel = resolvePaymentCurrencyLabel(
    latestPaymentCounter?.paymentCurrency,
    latestPaymentCounter?.paymentCurrencyId,
    currencyOptions
  );

  const fieldOptions = {
    invoicingQuantity: invoiceQuantityOptions,
    paymentMode: modeOptions,
    paymentCurrency: currencyOptions,
    law: lawOptions,
    arbitration: arbitrationOptions,
  };

  const getFieldGroup = def =>
    DELAY_PENALTY_TERM_FIELDS.includes(def)
      ? {
          defs: DELAY_PENALTY_TERM_FIELDS,
          endpoint: 'procurement-counter-delay-penalty-term',
          latest: latestDelayPenaltyCounter,
        }
      : PAYMENT_TERM_FIELDS.includes(def)
        ? {
            defs: PAYMENT_TERM_FIELDS,
            endpoint: 'procurement-counter-payment-term',
            latest: latestPaymentCounter,
          }
        : {
            defs: COMPLIANCE_FIELDS,
            endpoint: 'procurement-counter-compliance',
            latest: latestComplianceCounter,
          };

  const getFieldLabel = (def, source) => {
    if (isNumberField(def)) {
      return formatCounterFieldValue(def, source?.[def.field]);
    }
    if (isFreeTextField(def)) return source?.[def.field];
    if (def.key === 'paymentCurrency') {
      return resolvePaymentCurrencyLabel(
        source?.paymentCurrency,
        source?.paymentCurrencyId,
        currencyOptions
      );
    }
    return resolvePaymentSelectLabel(
      source?.[def.entity],
      fieldOptions[def.key],
      source?.[def.field]
    );
  };

  // Nilai original; amount Delay Penalty → "Confirmed" bila Mitra confirm
  // tanpa counter.
  const getOriginalLabel = def =>
    isNumberField(def) && DELAY_PENALTY_TERM_FIELDS.includes(def)
      ? resolveDelayPenaltyAmountText({
          isConfirmed: isDelayPenaltyConfirmed && !latestDelayPenaltyCounter,
          buyerAmount: procurement?.[def.field],
        })
      : getFieldLabel(def, procurement);

  const isFieldCountered = (def, counter) => {
    if (!counter) return false;
    if (isNumberField(def)) {
      return !isSameNumber(counter[def.field], procurement?.[def.field]);
    }
    if (isFreeTextField(def)) {
      return !isSameHtml(counter[def.field], procurement?.[def.field]);
    }
    if (
      isSameId(
        counter[def.field],
        procurement?.[def.field],
        counter[def.entity],
        procurement?.[def.entity]
      )
    ) {
      return false;
    }
    const counterLabel = getFieldLabel(def, counter);
    return (
      counterLabel === '-' || counterLabel !== getFieldLabel(def, procurement)
    );
  };

  const getAgreedFieldFinal = def =>
    getAgreedFinal(fieldAgreements?.[def.agreeTypeResponse]);

  const openCounter = (title, groupDefs, defs) => {
    const { latest } = getFieldGroup(groupDefs[0]);
    setCounterEdit({
      title,
      groupDefs,
      defs,
      draft: Object.fromEntries(
        defs.map(def => [
          def.field,
          getCounterFieldValue(latest || procurement, def),
        ])
      ),
    });
  };

  const openFieldCounter = def =>
    openCounter(def.label, getFieldGroup(def).defs, [def]);

  const setDraftValue = (def, value) =>
    setCounterEdit(prev => {
      const draft = { ...prev.draft, [def.field]: value };
      // Mode tergantung Invoicing Quantity → reset jika belum di-agree.
      const mode = prev.defs.find(d => d.key === 'paymentMode');
      if (
        def.key === 'invoicingQuantity' &&
        mode &&
        !getAgreedFieldFinal(mode)
      ) {
        draft[mode.field] = '';
      }
      return { ...prev, draft };
    });

  const handleSaveFieldCounter = async () => {
    if (!counterEdit) return;
    const { defs, draft, groupDefs } = counterEdit;
    if (
      defs.length === 1 &&
      isFreeTextField(defs[0]) &&
      isSameHtml(draft[defs[0].field], '')
    ) {
      notify.warning('Please enter a counter value.');
      return;
    }
    if (
      defs.some(
        def => isNumberField(def) && !isPositiveAmount(draft[def.field])
      )
    ) {
      notify.warning('Delay penalty amount must be greater than 0.');
      return;
    }
    const { endpoint, latest } = getFieldGroup(groupDefs[0]);
    const row = buildFieldCounterRow(groupDefs, latest, procurement);
    defs.forEach(def => {
      const value = draft[def.field];
      const isEmpty = value == null || String(value).trim() === '';
      row[def.field] = isEmpty
        ? null
        : isNumberField(def)
          ? Number(value)
          : value;
    });
    const saved = await saveCounter(endpoint, row, latest?.id);
    if (saved) setCounterEdit(null);
  };

  // Field pilihan: nilai + Counter Value + Agree & Lock per field.
  const renderAgreeFieldBlock = def => {
    const { latest } = getFieldGroup(def);
    const agreedFinal = getAgreedFieldFinal(def);
    const canAgree =
      typeof onAgreeField === 'function' &&
      Boolean(agreedFinal || isFieldAgreeable(def, latest, procurement));
    return (
      <Col
        lg={COMPLIANCE_FIELDS.includes(def) ? 6 : 4}
        key={def.key}
        id={`payment-compliance-${def.key}`}
      >
        <DetailItem label={def.label} value={getOriginalLabel(def)} />
        {isFieldCountered(def, latest) && (
          <CounterValueBlock title='Counter Value'>
            <CounterDetailItem
              label={def.label}
              value={getFieldLabel(def, latest)}
            />
          </CounterValueBlock>
        )}
        {(canAgree || agreedFinal) && (
          <div className='mt-2'>
            <AgreeLockButton
              isLocked={Boolean(agreedFinal)}
              onLock={
                canAgree
                  ? locked => onAgreeField(def.agreeTypeResponse, locked)
                  : undefined
              }
              disabled={isAgreeSaving}
            />
            {agreedFinal && (
              <div className='small text-primary fw-semibold mt-1'>
                Agreed · Final: {agreedFinal}
              </div>
            )}
          </div>
        )}
      </Col>
    );
  };

  const renderGroup = (title, groupDefs, groupKey, groupRespond) => {
    const agreeDefs = groupDefs.filter(isGroupAgreeField);
    const allowCounter =
      typeof canCounterGroup === 'function' && canCounterGroup(groupKey);
    return (
      <>
        <Row className='gy-4 mb-3'>
          {agreeDefs.map(renderAgreeFieldBlock)}
          {allowCounter && (
            <Col lg={12} className='d-flex justify-content-end'>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-edit-line'
                style={COUNTER_BADGE_STYLE}
                onClick={() => openCounter(title, groupDefs, agreeDefs)}
                disabled={isSavingCounter}
              >
                Counter
              </ActionButton>
            </Col>
          )}
        </Row>
        {groupRespond}
        <Row className='gy-4 mt-1 mb-3'>
          {groupDefs.filter(isFreeTextField).map(renderFieldBlock)}
        </Row>
      </>
    );
  };

  const renderFieldBlock = def => {
    const { latest } = getFieldGroup(def);
    const isHtml = isFreeTextField(def);
    const isCountered = isFieldCountered(def, latest);
    // Free-text: Agree & Lock per field (19-21) wajib isi Final (HTML).
    const agreedHtml = getAgreedFinalHtml(
      fieldAgreements?.[def.agreeTypeResponse]
    );
    const canAgree =
      typeof onAgreeField === 'function' &&
      Boolean(agreedHtml || isFieldAgreeable(def, latest, procurement));
    const allowCounter =
      !agreedHtml &&
      typeof canCounterField === 'function' &&
      canCounterField(def);
    return (
      <Col lg={12} key={def.key} id={`payment-compliance-${def.key}`}>
        <div className='d-flex align-items-start justify-content-between gap-3'>
          <div className='flex-grow-1 min-w-0'>
            <DetailItem
              label={def.label}
              value={getFieldLabel(def, procurement)}
              isHtml={isHtml}
            />
          </div>
          {allowCounter && (
            <ActionButton
              variant='secondary'
              size='sm'
              icon='ri-edit-line'
              className='flex-shrink-0'
              style={COUNTER_BADGE_STYLE}
              onClick={() => openFieldCounter(def)}
              disabled={isSavingCounter}
            >
              {isCountered ? 'Edit Counter' : 'Counter'}
            </ActionButton>
          )}
        </div>
        {isCountered && (
          <CounterValueBlock
            title={allowCounter ? 'Proposed Counter Value' : 'Counter Value'}
          >
            <CounterDetailItem
              label={def.label}
              value={getFieldLabel(def, latest)}
              isHtml={isHtml}
            />
          </CounterValueBlock>
        )}
        {(canAgree || agreedHtml) && (
          <div className='mt-2'>
            <AgreeLockButton
              isLocked={Boolean(agreedHtml)}
              finalValue={agreedHtml}
              // Prefill Final = counter Mitra terakhir.
              finalValuePrefill={String(latest?.[def.field] ?? '')}
              onLock={
                canAgree
                  ? (locked, finalValue) =>
                      onAgreeField(def.agreeTypeResponse, locked, finalValue)
                  : undefined
              }
              disabled={isAgreeSaving}
            />
          </div>
        )}
        {renderFieldRespond(def)}
      </Col>
    );
  };

  return (
    <>
      <SectionCard>
        <SectionTitle
          title='Delay Penalty Terms'
          subtitle='Review the penalty that applies when delivery is late.'
        />
        <DefaultAgreeNoticeAlert />
        {isPerField ? (
          renderGroup(
            'Delay Penalty Terms',
            DELAY_PENALTY_TERM_FIELDS,
            'counterDelayPenaltyTerm',
            delayPenaltyRespond
          )
        ) : (
          <>
            <Row className='gy-4 mb-3'>
              {DELAY_PENALTY_TERM_FIELDS.map(def => (
                <Col lg={isNumberField(def) ? 4 : 12} key={def.key}>
                  <DetailItem
                    label={def.label}
                    value={getOriginalLabel(def)}
                    isHtml={isFreeTextField(def)}
                  />
                </Col>
              ))}
            </Row>
            {latestDelayPenaltyCounter && (
              <CounterValueBlock title='Counter Value'>
                <Row className='gy-4'>
                  {DELAY_PENALTY_TERM_FIELDS.map(def =>
                    isNumberField(def) ? (
                      <Col lg={4} key={def.key}>
                        <CounterDetailItem
                          label={def.label}
                          value={getFieldLabel(def, latestDelayPenaltyCounter)}
                          unchanged={
                            !isFieldCountered(def, latestDelayPenaltyCounter)
                          }
                        />
                      </Col>
                    ) : (
                      isFieldCountered(def, latestDelayPenaltyCounter) && (
                        <Col lg={12} key={def.key}>
                          <CounterDetailItem
                            label={def.label}
                            value={getFieldLabel(
                              def,
                              latestDelayPenaltyCounter
                            )}
                            isHtml
                          />
                        </Col>
                      )
                    )
                  )}
                </Row>
              </CounterValueBlock>
            )}
          </>
        )}
        {!isPerField && delayPenaltyRespond}
      </SectionCard>

      <SectionCard>
        <SectionTitle
          title='Payment Terms'
          subtitle='Choose the payment methods and terms that apply.'
        />
        <DefaultAgreeNoticeAlert />
        {isPerField ? (
          renderGroup(
            'Payment Terms',
            PAYMENT_TERM_FIELDS,
            'counterPaymentTerm',
            paymentRespond
          )
        ) : (
          <>
            <Row className='gy-4 mb-3'>
              <Col lg={4}>
                <DetailItem
                  label='Invoicing Quantity'
                  value={resolvePaymentSelectLabel(
                    procurement?.invoicingQuantity,
                    invoiceQuantityOptions,
                    procurement?.invoicingQuantityId
                  )}
                />
              </Col>
              <Col lg={4}>
                <DetailItem
                  label='Mode'
                  value={resolvePaymentSelectLabel(
                    procurement?.modeInvoicingQuantity,
                    modeOptions,
                    procurement?.modeInvoicingQuantityId
                  )}
                />
              </Col>
              <Col lg={4}>
                <DetailItem label='Currency' value={originalCurrencyLabel} />
              </Col>
              <Col lg={12}>
                <DetailItem
                  label='Description of Payment Term'
                  value={procurement?.descriptionOfPaymentTerms}
                  isHtml
                />
              </Col>
            </Row>
            {latestPaymentCounter && (
              <CounterValueBlock title='Counter Value'>
                <Row className='gy-4'>
                  <Col lg={4}>
                    <CounterDetailItem
                      label='Invoicing Quantity'
                      value={resolvePaymentSelectLabel(
                        latestPaymentCounter.invoicingQuantity,
                        invoiceQuantityOptions,
                        latestPaymentCounter.invoicingQuantityId
                      )}
                      unchanged={isSameId(
                        latestPaymentCounter.invoicingQuantityId,
                        procurement?.invoicingQuantityId,
                        latestPaymentCounter.invoicingQuantity,
                        procurement?.invoicingQuantity
                      )}
                    />
                  </Col>
                  <Col lg={4}>
                    <CounterDetailItem
                      label='Mode'
                      value={resolvePaymentSelectLabel(
                        latestPaymentCounter.modeInvoicingQuantity,
                        modeOptions,
                        latestPaymentCounter.modeInvoicingQuantityId
                      )}
                      unchanged={isSameId(
                        latestPaymentCounter.modeInvoicingQuantityId,
                        procurement?.modeInvoicingQuantityId,
                        latestPaymentCounter.modeInvoicingQuantity,
                        procurement?.modeInvoicingQuantity
                      )}
                    />
                  </Col>
                  <Col lg={4}>
                    <CounterDetailItem
                      label='Currency'
                      value={counterCurrencyLabel}
                      unchanged={
                        isSameId(
                          latestPaymentCounter.paymentCurrencyId,
                          procurement?.paymentCurrencyId,
                          latestPaymentCounter.paymentCurrency,
                          procurement?.paymentCurrency
                        ) ||
                        (counterCurrencyLabel !== '-' &&
                          counterCurrencyLabel === originalCurrencyLabel)
                      }
                    />
                  </Col>
                  {!isSameHtml(
                    latestPaymentCounter.descriptionOfPaymentTerms,
                    procurement?.descriptionOfPaymentTerms
                  ) && (
                    <Col lg={12}>
                      <CounterDetailItem
                        label='Description of Payment Term'
                        value={latestPaymentCounter.descriptionOfPaymentTerms}
                        isHtml
                      />
                    </Col>
                  )}
                </Row>
              </CounterValueBlock>
            )}
          </>
        )}
        {!isPerField && paymentRespond}
      </SectionCard>

      <SectionCard>
        <SectionTitle
          title='Compliance'
          subtitle='Confirm the compliance requirements for this procurement.'
        />
        <DefaultAgreeNoticeAlert />
        {isPerField ? (
          renderGroup(
            'Compliance',
            COMPLIANCE_FIELDS,
            'counterCompliance',
            complianceRespond
          )
        ) : (
          <>
            <Row className='gy-4 mb-3'>
              <Col lg={6}>
                <DetailItem
                  label='Law'
                  value={resolvePaymentSelectLabel(
                    procurement?.law,
                    lawOptions,
                    procurement?.lawId
                  )}
                />
              </Col>
              <Col lg={6}>
                <DetailItem
                  label='Arbitration'
                  value={resolvePaymentSelectLabel(
                    procurement?.arbitration,
                    arbitrationOptions,
                    procurement?.arbitrationId
                  )}
                />
              </Col>
              <Col lg={12}>
                <DetailItem
                  label='Other Terms & Condition'
                  value={procurement?.otherTermsAndCondition}
                  isHtml
                />
              </Col>
              <Col lg={12}>
                <DetailItem
                  label='Additional Notes'
                  value={procurement?.additionalNotes}
                  isHtml
                />
              </Col>
            </Row>
            {latestComplianceCounter && (
              <CounterValueBlock title='Counter Value'>
                <Row className='gy-4'>
                  <Col lg={6}>
                    <CounterDetailItem
                      label='Law'
                      value={resolvePaymentSelectLabel(
                        latestComplianceCounter.law,
                        lawOptions,
                        latestComplianceCounter.lawId
                      )}
                      unchanged={isSameId(
                        latestComplianceCounter.lawId,
                        procurement?.lawId,
                        latestComplianceCounter.law,
                        procurement?.law
                      )}
                    />
                  </Col>
                  <Col lg={6}>
                    <CounterDetailItem
                      label='Arbitration'
                      value={resolvePaymentSelectLabel(
                        latestComplianceCounter.arbitration,
                        arbitrationOptions,
                        latestComplianceCounter.arbitrationId
                      )}
                      unchanged={isSameId(
                        latestComplianceCounter.arbitrationId,
                        procurement?.arbitrationId,
                        latestComplianceCounter.arbitration,
                        procurement?.arbitration
                      )}
                    />
                  </Col>
                  {!isSameHtml(
                    latestComplianceCounter.otherTermsAndCondition,
                    procurement?.otherTermsAndCondition
                  ) && (
                    <Col lg={12}>
                      <CounterDetailItem
                        label='Other Terms & Condition'
                        value={latestComplianceCounter.otherTermsAndCondition}
                        isHtml
                      />
                    </Col>
                  )}
                  {!isSameHtml(
                    latestComplianceCounter.additionalNotes,
                    procurement?.additionalNotes
                  ) && (
                    <Col lg={12}>
                      <CounterDetailItem
                        label='Additional Notes'
                        value={latestComplianceCounter.additionalNotes}
                        isHtml
                      />
                    </Col>
                  )}
                </Row>
              </CounterValueBlock>
            )}
          </>
        )}
        {!isPerField && complianceRespond}
      </SectionCard>

      {counterEdit && (
        <Modal
          isOpen
          toggle={() => setCounterEdit(null)}
          centered
          size='lg'
          backdrop='static'
          scrollable
        >
          <ModalHeader toggle={() => setCounterEdit(null)}>
            Counter {counterEdit.title}
          </ModalHeader>
          <ModalBody>
            <FormGrid columns={counterEdit.defs.length}>
              {counterEdit.defs.map(def =>
                isNumberField(def) ? (
                  <TextField
                    key={def.key}
                    label={def.label}
                    name={`counter-${def.key}`}
                    type='number'
                    min='0'
                    step='0.01'
                    suffix={def.unit}
                    value={counterEdit.draft[def.field]}
                    mode={getAgreedFieldFinal(def) ? 'disabled' : 'editable'}
                    hint={
                      getAgreedFieldFinal(def) ? 'Agreed — locked' : undefined
                    }
                    onChange={e => setDraftValue(def, e.target.value)}
                  />
                ) : isFreeTextField(def) ? (
                  <RespondNotesEditor
                    key={def.key}
                    id={`counter-${def.key}`}
                    value={counterEdit.draft[def.field] || ''}
                    onChange={e => setDraftValue(def, e.target.value)}
                  />
                ) : (
                  <SelectField
                    key={def.key}
                    label={def.label}
                    name={def.field}
                    options={fieldOptions[def.key] ?? []}
                    value={counterEdit.draft[def.field]}
                    mode={getAgreedFieldFinal(def) ? 'disabled' : 'editable'}
                    hint={
                      getAgreedFieldFinal(def) ? 'Agreed — locked' : undefined
                    }
                    onChange={value => setDraftValue(def, value ?? '')}
                  />
                )
              )}
            </FormGrid>
            <FormActions>
              <ActionButton
                variant='secondary'
                icon='ri-close-line'
                onClick={() => setCounterEdit(null)}
                disabled={isSavingCounter}
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant='primary'
                onClick={handleSaveFieldCounter}
                loading={isSavingCounter}
                disabled={isSavingCounter}
              >
                Save
              </ActionButton>
            </FormActions>
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default PaymentComplianceView;
